/*
 * @Author: Ryan
 * @Date: 2026-02-03
 * @Description: How-to section showing image enhancement workflow
 */
import Image from 'next/image';
import React from 'react';

interface StepCardProps {
  index: number;
  title: string;
  description: string;
}

const steps = [
  {
    title: 'Upload Your Image',
    description:
      "Click Upload or drag your photo into FalcoCut's enhancer. JPG and PNG files are supported, so you can start right away.",
  },
  {
    title: 'Choose the Resolution',
    description:
      'Select the output resolution you want. Higher options give you more detail and a sharper final image.',
  },
  {
    title: 'Let the AI Enhance Your Photo',
    description:
      'Click Start and FalcoCut will sharpen details, reduce blur, and boost clarity automatically. No manual editing needed.',
  },
  {
    title: 'Preview and Download',
    description:
      'Review the enhanced image in the preview. When it looks right, download the HD version to your device.',
  },
];

const StepCard: React.FC<StepCardProps> = ({ index, title, description }) => {
  return (
    <div className="flex gap-4 rounded-2xl p-4 pad:p-5">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#6366F1] text-white flex items-center justify-center font-semibold text-sm">
        {index}
      </div>
      <div>
        <h3 className="text-base pad:text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-xs pad:text-sm text-gray-600 mt-1">{description}</p>
      </div>
    </div>
  );
};

const HowToUseSection: React.FC = () => {
  return (
    <section className="w-full py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 mt-4 mb-8 text-center">
          How to Enhance Photo Quality With FalcoCut
        </h2>

        <div className="max-w-[1010px] mx-auto flex flex-col sm:flex-row items-center gap-8 pad:gap-10 sm:gap-12">
          {/* Visual */}
          <div className="flex-1">
            <div className="rounded-2xl overflow-hidden">
              <Image
                src="https://assets.falcocut.ai/ai-image-enhancer/how-to-enhance-photo-quality.webp"
                alt="How to Enhance Photo Quality With FalcoCut"
                width={440}
                height={520}
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
          </div>
          {/* Steps */}
          <div className="flex-1 w-full">
            <div className="space-y-4">
              {steps.map((step, index) => (
                <StepCard
                  key={step.title}
                  index={index + 1}
                  title={step.title}
                  description={step.description}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToUseSection;
